/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import {
    type Command, type PromptConfigFull, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract,
    UNPLACEHOLDER, UtilService,
} from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { NFSShare } from 'app/common/types/mo';
import { ProductConfig, SWITCH } from 'app/config/switch';

import { type NFSShareVm } from '../../share';
import { NfsShareStrService } from '../nfs-share-str.service';
import { NfsShareService } from './../nfs-share.service';

@Component({
    selector: 'app-modify-nfs-share',
    templateUrl: './modify-nfs-share.component.html',
    styleUrls: ['./modify-nfs-share.component.scss'],
})
export class ModifyNfsShareComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() data: NFSShare;

    i18n = this.str.i18n;
    closeArgs = { refresh: false };
    UNPLACEHOLDER = UNPLACEHOLDER;
    SWITCH = SWITCH;
    ProductConfig = ProductConfig;

    vm: FormGroup;
    errorMessage: any;

    charEncodingOpts: SelectOption[] = this.str.charEncodingOpts;

    get vmValue(): NFSShareVm {
        return this.vm ? this.vm.value : null;
    }

    constructor(
        injector: Injector,
        public str: NfsShareStrService,
        private toolService: ToolService,
        private NFSService: NfsShareService,
        private utilService: UtilService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.pageReady = true;
        this.windowVisible = true;
    }

    initVm(): void {
        this.vm = new FormGroup({
            DESCRIPTION: new FormControl(this.utilService.decodeHTML(this.data.DESCRIPTION), [
                DMValidators.rangeByteLength(0, 255),
            ]),
            CHARACTERENCODING: new FormControl(this.data.CHARACTERENCODING),
            ENABLESHOWSNAPSHOT: new FormControl(this.data.ENABLESHOWSNAPSHOT),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            DESCRIPTION: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 255),
            },
        };
    }

    prompt(): PromptConfigFull[] {
        const prompts: PromptConfigFull[] = [];

        if (this.vm.value.CHARACTERENCODING !== this.data.CHARACTERENCODING) {
            prompts.push({
                id: 'prompt-modify-nfs-share-modifyCodeingNFS',
                opType: 'warning',
                title: this.i18n.title.modifyShare,
                content: this.i18n.highRisk.modifyCodeingNFS,
                width: 700,
            });
        }
        return prompts;
    }

    buildCmds(): Command[] {
        const cmds: Command[] = [this.buildNFSCmd()];
        return cmds;
    }

    /**
     * 构建修改共享的rest
     */
    buildNFSCmd(): Command {
        const vmValue = this.vmValue;
        const params: NFSShare = {
            DESCRIPTION: vmValue.DESCRIPTION,
            CHARACTERENCODING: this.vm.value.CHARACTERENCODING,
            ENABLESHOWSNAPSHOT: this.vm.value.ENABLESHOWSNAPSHOT,
        };

        if (ProductConfig.isHideNfsSnapShot) {
            delete params.ENABLESHOWSNAPSHOT;
        }

        if (this.data.vstoreId) {
            params.vstoreId = this.data.vstoreId;
        }

        return {
            name: this.i18n.rest.modifyShare,
            nameParams: [this.data.SHAREPATH],
            action: this.NFSService.modify(this.data.ID, params),
        };
    }
}
